import { variantOperation } from "./variant-review";
import {
  type ConflictDetails,
  preconditionsSchema,
} from "./agent-preconditions";
import { layoutOperations } from "./agent-layout";
import { imageOperations } from "./agent-images";
import { tableOperations } from "./agent-tables";
import { pageOperations } from "./agent-pages";
import { galleryCaption } from "./image-caption";
import { z } from "zod";
import {
  workspaceOperationSchemas,
  canvasColors,
} from "./workspace-agent-contract";
import { SCENE_SPEC_MAX_CHARS } from "./schema";

const coordinate = z.number().finite().min(-100_000).max(100_000);
const length = z.number().finite().positive().max(10_000);
const shapeId = z.string().regex(/^shape:[a-zA-Z0-9_-]{1,100}$/);
const text = z.string().max(12_000);
const parent = {
  parentId: z
    .string()
    .regex(/^(shape|page):[a-zA-Z0-9_-]{1,100}$/)
    .optional(),
  sheetId: shapeId.optional(),
  pageId: z
    .string()
    .regex(/^page:[a-zA-Z0-9_-]{1,100}$/)
    .optional(),
};
const placement = { ...parent, x: coordinate, y: coordinate, w: length };
const color = z.union([canvasColors, z.string().regex(/^#[a-fA-F0-9]{6}$/)]);
const boldRanges = z
  .array(
    z
      .object({
        start: z.number().int().nonnegative().max(12000),
        end: z.number().int().positive().max(12000),
      })
      .strict(),
  )
  .max(100)
  .default([]);
const textStyle = {
  boldRanges,
  color: color.default("black"),
  bold: z.boolean().default(false),
  align: z.enum(["start", "middle", "end"]).default("start"),
};
export const agentSheet = z
  .object({
    type: z.literal("add_sheet"),
    parentId: z
      .string()
      .regex(/^page:[a-zA-Z0-9_-]{1,100}$/)
      .optional(),
    id: shapeId,
    pageId: z
      .string()
      .regex(/^page:[a-zA-Z0-9_-]{1,100}$/)
      .optional(),
    name: z.string().trim().min(1).max(200),
    x: coordinate,
    y: coordinate,
    w: length,
    h: length,
    paper: z.enum(["A4", "A3", "A2", "A1", "A0", "Custom"]).default("A3"),
    revisionLabel: z.string().max(80).default("01"),
  })
  .strict();
export const agentComment = z
  .object({
    type: z.literal("add_comment"),
    id: z.string().regex(/^[a-zA-Z0-9_-]{1,80}$/),
    text: text.min(1),
    anchor: z.union([
      z
        .object({
          shapeId,
          x: z.number().min(0).max(1),
          y: z.number().min(0).max(1),
        })
        .strict(),
      z
        .object({
          pageId: z.string().regex(/^page:[a-zA-Z0-9_-]{1,100}$/),
          x: coordinate,
          y: coordinate,
        })
        .strict(),
    ]),
    resolved: z.boolean().default(false),
    region: z
      .object({
        x: z.number().min(0).max(1),
        y: z.number().min(0).max(1),
        w: z.number().positive().max(1),
        h: z.number().positive().max(1),
      })
      .strict()
      .optional(),
    mentions: z
      .array(
        z
          .object({
            userId: z.string().min(1).max(128),
            label: z.string().min(1).max(120),
          })
          .strict(),
      )
      .max(20)
      .default([]),
    action: z
      .object({
        title: z.string().min(1).max(300),
        assignee: z.string().max(128).nullable(),
        due: z.string().nullable(),
      })
      .strict()
      .optional(),
  })
  .strict();
export const agentOperation = z.discriminatedUnion("type", [
  variantOperation,
  ...workspaceOperationSchemas.filter((s) => s.shape.type.value !== "resize_sheet"),
  ...pageOperations,
  ...imageOperations,
  ...layoutOperations,
  z
    .object({
      type: z.literal("set_color"),
      id: shapeId,
      color,
      strokeColor: z
        .string()
        .regex(/^#[a-fA-F0-9]{6}$/)
        .optional(),
      fillColor: z
        .string()
        .regex(/^#[a-fA-F0-9]{6}$/)
        .optional(),
    })
    .strict(),
  agentSheet,
  agentComment,
  z
    .object({
      type: z.literal("add_geometry"),
      id: shapeId,
      ...placement,
      h: length,
      geo: z.enum(["rectangle", "ellipse"]),
      color: color.default("black"),
      fill: z.enum(["none", "solid"]).default("none"),
      size: z.enum(["s", "m", "l", "xl"]).default("m"),
    })
    .strict(),
  z
    .object({
      type: z.literal("add_line"),
      id: shapeId,
      ...parent,
      x: coordinate,
      y: coordinate,
      x2: coordinate,
      y2: coordinate,
      color: z
        .string()
        .regex(/^#[a-fA-F0-9]{6}$/)
        .default("#1d1d1d"),
      weight: z.number().min(0.25).max(24).default(2),
    })
    .strict(),
  z
    .object({
      type: z.literal("add_text"),
      id: shapeId,
      ...placement,
      ...textStyle,
      text,
      fontSize: z.number().min(8).max(96).default(16),
    })
    .strict(),
  z
    .object({
      type: z.literal("update_text"),
      richText: z.unknown().optional(),
      formatting: z.enum(["preserve", "replace"]).optional(),
      bold: z.boolean().optional(),
      boldRanges: boldRanges.removeDefault().optional(),
      id: shapeId,
      text,
      fontSize: z.number().min(8).max(96).optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("edit_text"),
      id: shapeId,
      start: z.number().int().min(0).max(12000),
      end: z.number().int().min(0).max(12000),
      text: z.string().max(12000),
    })
    .strict(),
  z
    .object({
      type: z.literal("move"),
      id: shapeId,
      x: coordinate,
      y: coordinate,
    })
    .strict(),
  z
    .object({
      type: z.literal("resize"),
      id: shapeId,
      w: length,
      h: length.optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("add_image"),
      caption: galleryCaption.optional(),
      id: shapeId,
      ...placement,
      h: length,
      assetId: z.string().regex(/^asset:[a-zA-Z0-9_-]{1,100}$/),
      src: z
        .string()
        .min(1)
        .max(250)
        .regex(
          /^\/api\/rooms\/[a-zA-Z0-9_-]{1,80}\/files\/[a-zA-Z0-9_-]{1,80}$/,
        ),
      pixelWidth: z.number().int().positive().max(40_000),
      pixelHeight: z.number().int().positive().max(40_000),
      mimeType: z.enum(["image/png", "image/jpeg", "image/webp"]),
      name: z.string().min(1).max(200),
      provenance: z
        .object({
          sourceId: z.string().max(200).optional(),
          filePath: z.string().max(4000).optional(),
          generationId: z.string().max(200).optional(),
          revision: z.string().max(200).optional(),
          kind: z.enum([
            "web_image",
            "generated_image",
            "uploaded_image",
            "canvas_image",
          ]),
          url: z.string().url().startsWith("https://").max(2000).optional(),
          pageUrl: z.string().url().startsWith("https://").max(2000).optional(),
          author: z.string().max(300).optional(),
          licence: z.string().max(100).optional(),
          prompt: z.string().max(4000).optional(),
          sourceIds: z.array(z.string().max(150)).max(10).optional(),
          provider: z.string().max(40).optional(),
          project: z.string().max(100).optional(),
        })
        .strict()
        .optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("add_scene"),
      id: shapeId,
      ...placement,
      h: length,
      name: z.string().min(1).max(200),
      /** JSON of a SceneSpec; validated when applied. A string keeps tuples out of tool schemas. */
      spec: z.string().min(2).max(SCENE_SPEC_MAX_CHARS),
    })
    .strict(),
  z
    .object({
      type: z.literal("update_scene"),
      id: shapeId,
      /** JSON of the edited SceneSpec; validated when applied. */
      spec: z.string().min(2).max(SCENE_SPEC_MAX_CHARS),
      name: z.string().min(1).max(200).optional(),
    })
    .strict(),
]);
export const agentBatch = z
  .object({
    batchId: z.string().uuid(),
    revision: z.number().int().nonnegative(),
    gallery: z.boolean().optional(),
    preconditions: preconditionsSchema.optional(),
    operations: z.array(agentOperation).min(1).max(50),
  })
  .strict()
  .superRefine((batch, ctx) => {
    if (batch.gallery && batch.operations.some((op) => op.type !== "add_image"))
      ctx.addIssue({
        code: "custom",
        message:
          "Use up to 50 operations; gallery batches contain only image cards.",
      });
  });
// All batches are atomic and bounded by 50 operations and the transport byte limit.
export const agentUndo = z
  .object({
    batchId: z.string().uuid(),
    revision: z.number().int().nonnegative(),
    undoBatchId: z.string().uuid(),
  })
  .strict();
export type AgentBatch = z.infer<typeof agentBatch>;
export type AgentUndo = z.infer<typeof agentUndo>;
export class AgentError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}
export class AgentConflict extends AgentError {
  constructor(
    message: string,
    readonly conflict: ConflictDetails,
  ) {
    super(409, message);
  }
}
export const agentFailure = (
  error: unknown,
): { status: number; error: string; conflict?: ConflictDetails } =>
  error instanceof AgentError
    ? {
        status: error.status,
        error: error.message,
        ...(error instanceof AgentConflict ? { conflict: error.conflict } : {}),
      }
    : {
        status: 400,
        error:
          "Invalid board edit. Read the current sheet and check the operation.",
      };
